import { FC, useState, useEffect } from 'react'
import { EmptyState, PermissionIcon, theme } from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general'
import { ResidentStatus, UserRole, CernerRole } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import useResident from 'hooks/useResident'
import CernerHIE from './CernerHIE'
import { useSelector } from 'react-redux'
import { User, Home, ResidentItem } from '@lmc/ts-common'
import { cernerConstants } from 'locale/cerner'

const SCR_ACCESS_CONFIRMED_SESSION_KEY = 'scr_access_confirmed'

type Props = {}

type ValidationCheckProps = {
  resident: ResidentItem
  hasSharedCareRecordEnabled: boolean | undefined
  hasSharedCareRecordPermission: boolean | undefined
  hasCernerRolePermission: boolean | undefined
  children: React.ReactNode
}

const ValidationCheck: FC<ValidationCheckProps & { testId?: string }> = ({
  resident,
  hasSharedCareRecordEnabled,
  hasSharedCareRecordPermission,
  hasCernerRolePermission,
  children,
  testId,
}) => {
  const { t } = useTranslation(NAMESPACE.CERNER)

  // Check for Clinical Role
  if (!hasSharedCareRecordPermission) {
    return (
      <EmptyState
        title={t(cernerConstants.PERMISSION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.PERMISSION_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-permission-error`
            : 'shared-care-record-permission-error'
        }
      />
    )
  }

  // Check for Cerner Role Permission
  if (!hasCernerRolePermission) {
    return (
      <EmptyState
        title={t(cernerConstants.PERMISSION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.INSUFFICIENT_CERNER_ROLE_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-cerner-role-error`
            : 'shared-care-record-cerner-role-error'
        }
      />
    )
  }

  // Check for ODS Code
  if (!hasSharedCareRecordEnabled) {
    return (
      <EmptyState
        title={t(cernerConstants.SITE_CONFIGURATION_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.SITE_CONFIGURATION_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-site-config-error`
            : 'shared-care-record-site-config-error'
        }
      />
    )
  }

  // Check for Active Service User
  if (resident.status !== ResidentStatus.active) {
    return (
      <EmptyState
        title={t(cernerConstants.INACTIVE_SERVICE_USER)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.INACTIVE_SERVICE_USER_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-inactive-user-error`
            : 'shared-care-record-inactive-user-error'
        }
      />
    )
  }

  // Check for NHS Number
  if (!resident.nhsNumber) {
    return (
      <EmptyState
        title={t(cernerConstants.NHS_NUMBER_REQUIRED)}
        icon={<PermissionIcon />}
        description={t(cernerConstants.NHS_NUMBER_DESCRIPTION)}
        data-testid={
          testId
            ? `${testId}-shared-care-record-nhs-number-error`
            : 'shared-care-record-nhs-number-error'
        }
      />
    )
  }

  return children
}

export const SharedCareRecordScreen: FC<Props & { testId?: string }> = ({
  testId,
}) => {
  const { t } = useTranslation(NAMESPACE.CERNER)
  const {
    params: { id },
  } = useRouter()

  const { resident } = useResident({ id })
  const [hasConfirmedAccess, setHasConfirmedAccess] = useState(false)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user,
  )

  const hasSharedCareRecordEnabled = !!home?.odsCode
  const hasSharedCareRecordPermission =
    user?.role === UserRole.seniorCarer || user?.role === UserRole.careHomeAdmin
  const hasCernerRolePermission =
    !!user?.cernerRole &&
    [
      CernerRole.SeniorCarer,
      CernerRole.Admin,
      CernerRole.RegisteredManager,
    ].includes(user?.cernerRole)

  useEffect(() => {
    try {
      const hasConfirmed =
        sessionStorage.getItem(SCR_ACCESS_CONFIRMED_SESSION_KEY) === 'true'
      if (hasConfirmed) {
        setHasConfirmedAccess(true)
      }
    } catch (error) {
      console.warn('Failed to read from sessionStorage:', error)
    }
  }, [])

  const handleConfirmAccess = () => {
    try {
      sessionStorage.setItem(SCR_ACCESS_CONFIRMED_SESSION_KEY, 'true')
    } catch (error) {
      console.error('Failed to save confirmation to sessionStorage:', error)
    }
    setHasConfirmedAccess(true)
  }

  if (!resident) {
    return (
      <div
        data-testid={
          testId
            ? `${testId}-shared-care-record-loading`
            : 'shared-care-record-loading'
        }
      >
        Loading...
      </div>
    )
  }

  return (
    <MainContainer
      data-testid={
        testId
          ? `${testId}-shared-care-record-container`
          : 'shared-care-record-container'
      }
    >
      <SCRContainer
        data-testid={
          testId
            ? `${testId}-shared-care-record-content`
            : 'shared-care-record-content'
        }
      >
        <ValidationCheck
          resident={resident}
          hasSharedCareRecordEnabled={hasSharedCareRecordEnabled}
          hasSharedCareRecordPermission={hasSharedCareRecordPermission}
          hasCernerRolePermission={hasCernerRolePermission}
          testId={testId}
        >
          {!hasConfirmedAccess ? (
            <AccessConfirmationContainer>
              <AccessConfirmationCard>
                <AccessConfirmationTitle>
                  {t(cernerConstants.ACCESS_CONFIRMATION_TITLE)}
                </AccessConfirmationTitle>
                <AccessConfirmationDescription>
                  {t(cernerConstants.ACCESS_CONFIRMATION_MESSAGE)}
                </AccessConfirmationDescription>
                <AccessConfirmationList>
                  <AccessConfirmationListItem>
                    {t(cernerConstants.ACCESS_CONFIRMATION_POINT_1)}
                  </AccessConfirmationListItem>
                  <AccessConfirmationListItem>
                    {t(cernerConstants.ACCESS_CONFIRMATION_POINT_2)}
                  </AccessConfirmationListItem>
                  <AccessConfirmationListItem>
                    {t(cernerConstants.ACCESS_CONFIRMATION_POINT_3)}
                  </AccessConfirmationListItem>
                  <AccessConfirmationListItem>
                    {t(cernerConstants.ACCESS_CONFIRMATION_POINT_4)}
                  </AccessConfirmationListItem>
                </AccessConfirmationList>
                <ButtonContainer>
                  <Button variant="primary" onClick={handleConfirmAccess}>
                    {t(cernerConstants.CONFIRM)}
                  </Button>
                </ButtonContainer>
              </AccessConfirmationCard>
            </AccessConfirmationContainer>
          ) : (
            <IframeWrapper
              data-testid={
                testId
                  ? `${testId}-shared-care-record-iframe-wrapper`
                  : 'shared-care-record-iframe-wrapper'
              }
            >
              <CernerHIE residentId={resident.id} />
            </IframeWrapper>
          )}
        </ValidationCheck>
      </SCRContainer>
    </MainContainer>
  )
}

const MainContainer = styled.div`
  min-width: 1024px; // NHSx requirement
  min-height: 768px; // NHSx requirement
  height: 100vh;
  display: flex;
  flex-direction: column;
`

const SCRContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const IframeWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`

const AccessConfirmationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 20px;
  min-height: 600px;
`

const AccessConfirmationCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 40px;
  max-width: 600px;
  width: 100%;
`

const AccessConfirmationTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${theme.colors?.grey?.[900] || '#1a1a1a'};
`

const AccessConfirmationDescription = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
  color: ${theme.colors?.grey?.[700] || '#4a4a4a'};
  white-space: pre-line;
`

const AccessConfirmationList = styled.ul`
  padding-left: 24px;
  list-style-type: disc;
`

const AccessConfirmationListItem = styled.li`
  font-size: 16px;
  margin-bottom: 12px;
  color: ${theme.colors?.grey?.[700] || '#4a4a4a'};
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`
